import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import {
  CompanyVendor,
  CompanyVendorPayment,
  User
} from 'src/app/classes/models';
import { ViveProgress } from 'src/app/classes/models/vive-progress';
import { ComplianceSteps } from 'src/app/interfaces/compliance-steps';
import { ViveProgressStatus } from 'src/app/interfaces/vive-progress-status';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-compliance-tracker',
  templateUrl: './compliance-tracker.component.html',
  styleUrls: ['./compliance-tracker.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceTrackerComponent implements OnInit, OnChanges {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private router: Router,
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private AuthService: AuthService
  ) {
    this.LoggedInUser = this.AuthService.getUser();
  }

  ComplianceProgressMap: Map<ComplianceSteps, ViveProgress | null> = new Map([
    ['Vendor Info', null],
    ['Owner Info', null],
    ['Trades & Licenses', null],
    ['Insurance', null]
  ]);
  ComplianceSteps: ComplianceSteps[] = [];
  ComplianceStatusToClassMap: Map<ViveProgressStatus, string> = new Map([
    ['Missing', 'missing'],
    ['Reviewing', 'started'],
    ['Resolving', 'started'],
    ['Passed', 'complete'],
    ['Failed', 'failed'],
    ['Review Needed', 'missing']
  ]);
  ComplianceStepToRouteMap: Map<ComplianceSteps, string> = new Map([
    ['Vendor Info', 'business-review'],
    ['Owner Info', 'owner-review'],
    ['Trades & Licenses', 'trade-review'],
    ['Insurance', 'insurance-review']
  ]);

  // Can bind updated Compliance Progress directly.
  @Input() CurrentComplianceProgress: ViveProgress | null = null;
  @Input() CurrentComplianceStep?: ComplianceSteps;
  @Input() CompanyVendorId!: number;
  @Input() CompanyVendor!: CompanyVendor;
  @Input() action?: 'review' | 'edit';
  @Input() allowSelection: boolean = false;
  @Output() CurrentComplianceStepChange = new EventEmitter<ComplianceSteps>();
  CompanyId!: number;
  VendorId!: number;
  CompanyComplianceTypeId!: number;
  // Used to determine if a Vendor made a recent, valid payment. Displayed as the first part of
  // the compliance tracker.
  RecentPendingOrPaidCompanyVendorPayment?: CompanyVendorPayment;
  LoggedInUser: User;

  // A fee voucher satisfies the payment step outright, so the flag alone decides.
  // Do not gate it on `renewalDate`: applying a voucher leaves `vendorPaid` set,
  // and for a paid relationship that alias is the old payment's expDate, so a
  // voucher dated after it would read as an expired payment.
  isVendorFeeWaived(): boolean {
    return !!this.CompanyVendor?.feeWaived;
  }

  getPaymentInformation() {
    if (this.isVendorFeeWaived()) {
      return 'YES - FEE WAIVED';
    }

    if (!this.RecentPendingOrPaidCompanyVendorPayment) {
      return 'NO';
    }

    const { expStatus, expDate, Invoice } =
      this.RecentPendingOrPaidCompanyVendorPayment;

    if (!Invoice) {
      return 'NO';
    }

    const { paidDate, status } = Invoice;

    if (status === 'PENDING') {
      return 'PENDING';
    }

    if (
      !status ||
      !['PAID', 'PENDING'].includes(status) ||
      !paidDate ||
      !expStatus
    ) {
      return 'NO';
    }

    if (['12M', 'VALID', '30D', '15D', '10D', '5D', '1D'].includes(expStatus)) {
      return DateTime.fromJSDate(paidDate).toFormat('MM/dd/yyyy').toString();
    }

    if (expStatus === 'EXPIRED') {
      return 'EXPIRED';
    }

    return 'NO';
  }

  getPaymentStatusClass() {
    if (this.isVendorFeeWaived()) {
      return 'complete';
    }

    if (!this.RecentPendingOrPaidCompanyVendorPayment) {
      return 'not-started';
    }

    const { expStatus, Invoice } = this.RecentPendingOrPaidCompanyVendorPayment;

    if (!Invoice) {
      return 'not started';
    }

    if (!expStatus) {
      return 'not-started';
    }

    const { status } = Invoice;

    if (
      status &&
      ['PAID', 'PENDING'].includes(status) &&
      ['12M', 'VALID', '30D', '15D', '10D', '5D', '1D'].includes(expStatus)
    ) {
      return 'complete';
    }

    return 'failed';
  }

  getProgressDate(ComplianceStep: ComplianceSteps) {
    const ComplianceProgress = this.ComplianceProgressMap.get(ComplianceStep);
    if (ComplianceProgress) {
      const { statusDate } = ComplianceProgress;

      if (statusDate) {
        return DateTime.fromJSDate(statusDate)
          .toFormat('MM/dd/yyyy')
          .toString();
      }
    }

    return '';
  }

  getProgressStatusClass(ComplianceStep: ComplianceSteps) {
    const ComplianceProgress = this.ComplianceProgressMap.get(ComplianceStep);

    if (ComplianceProgress) {
      const { status } = ComplianceProgress;
      if (!status) {
        return 'not-started';
      }

      return this.ComplianceStatusToClassMap.get(status);
    } else {
      return 'not-started';
    }
  }

  goToStep(ComplianceStep: ComplianceSteps) {
    if (!this.action) {
      throw this.ToastService.addErrorToast('Error', 'Missing review action.', {
        returnError: true
      });
    }

    const navArray = [
      'admin',
      'run-vive',
      'client-vendor',
      this.CompanyVendorId,
      'client',
      this.CompanyId,
      'vendor',
      this.VendorId,
      'compliance-type',
      this.CompanyComplianceTypeId,
      'action',
      this.action
    ];

    const route = this.ComplianceStepToRouteMap.get(ComplianceStep);

    if (!route) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing route information.',
        { returnError: true }
      );
    }

    this.CurrentComplianceStep = ComplianceStep;
    this.CurrentComplianceStepChange.emit(ComplianceStep);
    navArray.push(route);
    this.router.navigate(navArray);
  }

  getComplianceStepText(ComplianceStep: ComplianceSteps) {
    const ComplianceProgress = this.ComplianceProgressMap.get(ComplianceStep);

    const textArray: string[] = [ComplianceStep, '-'];

    if (ComplianceProgress) {
      const { status, subStatus } = ComplianceProgress;

      if (!status) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing compliance progress status.',
          { returnError: true }
        );
      }

      textArray.push(status.toUpperCase());

      // Omit "No Licensed Trades" from the progress bar — that detail belongs
      // on the license details card as "No license requirements".
      if (subStatus && subStatus !== 'No Licensed Trades') {
        textArray.push('-', subStatus.toUpperCase());
      }
    } else {
      textArray.push('NOT STARTED');
    }

    return textArray.join(' ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { CurrentComplianceProgress } = changes;

    if (CurrentComplianceProgress) {
      const { currentValue }: { currentValue: ViveProgress | null } =
        CurrentComplianceProgress;

      if (currentValue) {
        const { type } = currentValue;

        if (!type) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Compliance Progress is missing type.',
            { returnError: true }
          );
        }

        this.ComplianceProgressMap.set(type, currentValue);
      }
    }
  }

  ngOnInit(): void {
    if (!this.CompanyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor id.',
        { returnError: true }
      );
    }

    this.CompanyVendorService.getCompanyVendor({
      attributes: JSON.stringify([
        'id',
        'CompanyId',
        'VendorId',
        'country',
        'CompanyComplianceTypeId',
        'feeWaived',
        'feeWaivedDate',
        'lastRenewalAnniversaryDate'
      ]),
      aliasedAttributes: JSON.stringify(['renewalDate']),
      id: this.CompanyVendorId,
      isArchived: JSON.stringify({ $in: [true, false] }),
      ViveProgress: JSON.stringify({
        options: { required: false }
      }),
      Company: JSON.stringify({
        options: { required: true },
        attributes: ['id', 'fullServiceOnsite', 'fullServiceOffsite']
      }),
      Vendor: JSON.stringify({
        options: { required: true },
        attributes: JSON.stringify(['id', 'isPubliclyTraded'])
      }),
      RecentPendingOrPaidCompanyVendorPayment: JSON.stringify({
        options: { required: false },
        Invoice: {}
      })
    }).subscribe({
      next: (CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor information.',
            { returnError: true }
          );
        }

        const {
          CompanyId,
          VendorId,
          country,
          CompanyComplianceTypeId,
          Company,
          Vendor,
          ViveProgresses,
          RecentPendingOrPaidCompanyVendorPayment
        } = CompanyVendor;

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        if (!VendorId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing country information.',
            { returnError: true }
          );
        }

        if (!CompanyComplianceTypeId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance Type Id.',
            { returnError: true }
          );
        }

        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        const { isPubliclyTraded } = Vendor;
        const { fullServiceOnsite, fullServiceOffsite } = Company;

        const { UserAccountRole: { role } = {} } = this.LoggedInUser;

        if (!role) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing user permission.',
            { returnError: true }
          );
        }

        // Limit the users that can access the owner step.
        const AcceptableOwnerStepPermissions = new Set([
          'ADMIN_ADMIN',
          'VENDOR_ADMIN',
          'CLIENT_ADMIN',
          'CLIENT_VENDOR_AND_SITES_MANAGER',
          'ADMIN_FRAUD_TEAM',
          'ADMIN_CUSTOMER_SERVICE_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]);

        // An SSN waives the finding inside the step, and Canada keeps the step.
        // A publicly traded company has no Owner Info review.
        const excludeOwnerStep =
          (!fullServiceOnsite && !fullServiceOffsite) ||
          isPubliclyTraded ||
          !AcceptableOwnerStepPermissions.has(role);

        if (!excludeOwnerStep) {
          this.ComplianceSteps.push(
            'Vendor Info',
            'Owner Info',
            'Trades & Licenses',
            'Insurance'
          );
        } else {
          this.ComplianceSteps.push(
            'Vendor Info',
            'Trades & Licenses',
            'Insurance'
          );
        }

        Object.assign(this, {
          CompanyId,
          VendorId,
          CompanyComplianceTypeId,
          RecentPendingOrPaidCompanyVendorPayment,
          CompanyVendor
        });

        if (Array.isArray(ViveProgresses)) {
          ViveProgresses.forEach((ViveProgress) => {
            const { type } = ViveProgress;

            if (!type) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Compliance Progress is missing type.',
                { returnError: true }
              );
            }
            this.ComplianceProgressMap.set(type, ViveProgress);
          });
        }

        this._cdr.markForCheck();
      }
    });
  }
}
